/**
 * /fo · Front Desk Command Centre (ADR-FO-1 §14)
 * The receptionist's home. 12 KPI tiles + Today's Work + Alerts + Search.
 * Every action deep-links into <GuestWorkspace>. F2 shortcut = search.
 */
import { useCallback, useEffect, useState } from "react";
import { AssignRoomButton } from "@/components/pms/AssignRoomButton";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  PrimaryActionBar, QuickActionTile, OperationalCard, TaskCard,
  StatusChip, TodaysWorkPanel, OperationalButton,
} from "@/components/operational";
import {
  UserPlus, Search, RefreshCw, BedDouble, LogIn, LogOut, Crown, Wrench,
  Sparkles, CreditCard, ShieldCheck, Users, Percent, CalendarClock, Printer,
} from "lucide-react";
import { foApi, money } from "./foApi";
import { requestRegCard } from "@/components/documents/RegCardOptions";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import FOGuestWorkspaceContainer from "./FOGuestWorkspaceContainer";
import WalkInDrawer from "./drawers/WalkInDrawer";
import GuestSearchDrawer from "./drawers/GuestSearchDrawer";
import { useStayCheckedOut } from "@/hooks/useStayCheckedOut";
import { useCheckinGate } from "@/hooks/useCheckinGate";

const TILE_ICONS = {
  rooms_ready: BedDouble,
  arrivals_today: LogIn,
  departures_today: LogOut,
  pending_checkins: LogIn,
  pending_checkouts: LogOut,
  vip_arrivals: Crown,
  engineering_holds: Wrench,
  hk_queue: Sparkles,
  outstanding_payments: CreditCard,
  pending_approvals: ShieldCheck,
  walkins_today: Users,
  current_occupancy: Percent,
};

const REFRESH_MS = 15_000;

export default function FrontDeskCommandCentre() {
  const gate = useCheckinGate();
  const [dashboard, setDashboard] = useState(null);
  const [todaysWork, setTodaysWork] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  // Follow the sidebar-selected active hotel (same pattern hkApi/fnbApi use).
  // Fallback chain: active_hotel_id cookie → user's primary hotel → first
  // hotel from /hotels. Previously we ignored the cookie which caused the
  // walk-in room picker to look at a random property for super admins.
  const activeHotelIdFromCookie = () => {
    if (typeof document === "undefined") return null;
    const c = document.cookie.split(";").map(x => x.trim())
      .find(x => x.startsWith("active_hotel_id="));
    return c ? decodeURIComponent(c.split("=")[1]) : null;
  };
  const [hotelId, setHotelId] = useState(
    activeHotelIdFromCookie() || user?.hotel_ids?.[0] || null
  );

  useEffect(() => {
    if (hotelId) return;
    api.get("/hotels").then(r => {
      const list = r.data?.items || r.data || [];
      if (list[0]?.id || list[0]?._id) setHotelId(list[0].id || list[0]._id);
    }).catch(() => {});
  }, [hotelId]);

  const [activeGuest, setActiveGuest] = useState(null); // {id, tab, action}
  const [walkInOpen, setWalkInOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [arrivalsDate, setArrivalsDate] = useState(() => new Date().toISOString().slice(0, 10));

  const printArrivals = () => {
    if (!hotelId) { toast.error("No active hotel selected"); return; }
    requestRegCard({ url: foApi.arrivalsRegistrationCardsUrl(hotelId, arrivalsDate), filename: `registration-cards-${arrivalsDate}.pdf`, title: "Arrival Registration Cards" });
  };

  const load = useCallback(async () => {
    try {
      const [d, t, a] = await Promise.all([
        foApi.dashboard({ hotel_id: hotelId }),
        foApi.todaysWork({ hotel_id: hotelId }),
        foApi.alerts({ hotel_id: hotelId }),
      ]);
      setDashboard(d);
      setTodaysWork(t);
      setAlerts(a.alerts || []);
    } catch (e) {
      toast.error("Failed to load Command Centre");
    } finally { setLoading(false); }
  }, [hotelId]);

  useEffect(() => { load(); }, [load]);
  useStayCheckedOut(load);
  useEffect(() => {
    const iv = setInterval(load, REFRESH_MS);
    return () => clearInterval(iv);
  }, [load]);

  // Deep-link: /fo?guest=<id>&tab=folio&action=quick-payment
  useEffect(() => {
    const gid = params.get("guest");
    if (gid) {
      setActiveGuest({
        id: gid,
        tab: params.get("tab") || "overview",
        action: params.get("action") || null,
      });
    } else {
      setActiveGuest(null);
    }
  }, [params]);

  // Keyboard shortcuts (AR-5)
  useEffect(() => {
    const handler = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      if (e.key === "F2") { e.preventDefault(); setSearchOpen(true); }
      if (e.key === "F4") { e.preventDefault(); setWalkInOpen(true); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const openGuest = (guestId, { tab = "overview", action = null } = {}) => {
    const p = new URLSearchParams(params);
    p.set("guest", guestId);
    if (tab) p.set("tab", tab); else p.delete("tab");
    if (action) p.set("action", action); else p.delete("action");
    setParams(p);
  };

  const closeGuest = () => {
    const p = new URLSearchParams(params);
    p.delete("guest"); p.delete("tab"); p.delete("action");
    setParams(p);
  };

  return (
    <div className="space-y-6" data-testid="fo-command-centre">
      <PrimaryActionBar
        title="Front Desk"
        subtitle="Operational Command Centre · Live view refreshes every 15s"
        actions={[
          { label: "Walk-In (F4)", icon: UserPlus, tone: "primary",
             onClick: () => setWalkInOpen(true), testid: "cc-walkin" },
          { label: "Search (F2)", icon: Search, tone: "secondary",
             onClick: () => setSearchOpen(true), testid: "cc-search" },
          { label: "Refresh", icon: RefreshCw, tone: "secondary",
             onClick: load, testid: "cc-refresh" },
        ]}
        testid="cc-actionbar"
      />

      {/* KPI tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="cc-tiles">
        {(dashboard?.tiles || []).map(tile => {
          const Icon = TILE_ICONS[tile.key] || BedDouble;
          const isMoney = tile.key === "outstanding_payments";
          const isPct = tile.unit === "pct";
          return (
            <QuickActionTile
              key={tile.key}
              label={tile.label}
              count={isMoney ? undefined : (isPct ? `${tile.count}%` : tile.count)}
              icon={Icon}
              status={tile.status}
              onClick={() => scrollToSection(tile.key)}
              testid={`cc-tile-${tile.key}`}
            />
          );
        })}
      </div>

      {/* Outstanding payments — friendly ₹ display separately */}
      {dashboard && (
        <OperationalCard tone="warning" testid="cc-outstanding-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Total Outstanding Balance</div>
              <div className="text-[26px] font-bold text-amber-900">
                {money(dashboard.tiles?.find(t => t.key === "outstanding_payments")?.amount_minor || 0)}
              </div>
            </div>
            <StatusChip status="attention" label="Live"/>
          </div>
        </OperationalCard>
      )}

      {/* Two-column layout: alerts + today's work */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-3" data-testid="cc-todays-work">
          <h2 className="text-[14px] font-bold uppercase tracking-wider text-slate-500">Today&apos;s Work</h2>
          <TodaysWorkPanel
            data={todaysWork?.sections || {}}
            renderTrailing={(key, it) => (["arrivals", "pending_checkins"].includes(key) && it.reservation && it.reservation.status !== "checked_in")
              ? <span onClick={(e) => e.stopPropagation()} data-testid={`tw-${key}-room-${it.reservation.code}`}><AssignRoomButton reservation={it.reservation} onAssigned={load} testIdPrefix="tw-" /></span> : null}
            loading={loading}
            onRowClick={(sectionKey, item) => {
              if (!item.guest_id) return;
              const isCheckedIn = (item.status || "") === "checked_in";
              const action = isCheckedIn ? null : actionForSection(sectionKey);
              openGuest(item.guest_id, { tab: sectionForTab(sectionKey), action });
            }}
            testid="cc-tw"
          />
        </div>

        <div className="space-y-3" data-testid="cc-alerts">
          <h2 className="text-[14px] font-bold uppercase tracking-wider text-slate-500">Alerts</h2>
          {alerts.length === 0 ? (
            <OperationalCard>
              <div className="text-[13px] text-slate-400 py-6 text-center">No active alerts</div>
            </OperationalCard>
          ) : alerts.slice(0, 20).map((al, i) => (
            <TaskCard
              key={i}
              title={al.title}
              sublabel={al.sublabel}
              status={al.status}
              statusLabel={al.priority}
              meta={[]}
              onOpen={() => {
                if (al.guest_id) openGuest(al.guest_id, {
                  tab: al.kind === "unpaid_departure" ? "folio" : "overview",
                  action: al.kind === "unpaid_departure" ? "quick-payment" : null,
                });
              }}
              testid={`cc-alert-${i}`}
            />
          ))}
        </div>
      </div>

      {/* Boards previews */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="cc-boards">
        <OperationalCard>
          <div className="mb-3 flex items-center justify-between gap-2 flex-wrap">
            <h3 className="text-[14px] font-bold uppercase tracking-wider text-slate-500">Arrivals · Top 5</h3>
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={arrivalsDate}
                onChange={(e) => setArrivalsDate(e.target.value)}
                aria-label="Arrivals date for bulk print"
                className="h-8 w-[150px] text-[12px]"
                data-testid="cc-arrivals-print-date"
              />
              <OperationalButton
                label="Print all arrivals"
                icon={Printer}
                tone="secondary"
                size="secondary"
                onClick={printArrivals}
                testid="cc-print-arrivals"
              />
            </div>
          </div>
          {(dashboard?.arrivals_preview || []).length === 0 ? (
            <div className="text-[13px] text-slate-400 py-4">No arrivals today</div>
          ) : dashboard.arrivals_preview.map((r, i) => (
            <div key={r.id}
              onClick={() => r.guest_id ? openGuest(r.guest_id, { tab: "overview", action: r.status === "checked_in" || gate.isBlocked(r.arrival_date) ? null : "check-in" }) : null}
              title={gate.isBlocked(r.arrival_date) ? gate.reason(r.arrival_date) : undefined}
              className="py-2 border-b border-slate-100 flex items-center gap-3 hover:bg-slate-50 -mx-2 px-2 cursor-pointer min-h-[48px]"
              data-testid={`cc-arrival-${i}`}
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium text-[14px] truncate">{r.guest_name}</div>
                <div className="text-[12px] text-slate-500 truncate">{r.code} · {r.arrival_time || ""}{r.room_number && (r.status === "checked_in" || r.is_multi_room) ? ` · Room ${r.room_number}` : ""}</div>
              </div>
              {r.status !== "checked_in" && !r.is_multi_room && (
                <span data-testid={`cc-arrival-room-${i}`}><AssignRoomButton reservation={r} onAssigned={load} showRoomWhenLocked={false} /></span>
              )}
              {r.vip && <StatusChip status="active" label="VIP" size="sm"/>}
              <StatusChip status={r.status === "checked_in" ? "active" : "pending"} label={r.status} size="sm"/>
            </div>
          ))}
        </OperationalCard>

        <OperationalCard>
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-[14px] font-bold uppercase tracking-wider text-slate-500">Departures · Top 5</h3>
          </div>
          {(dashboard?.departures_preview || []).length === 0 ? (
            <div className="text-[13px] text-slate-400 py-4">No departures today</div>
          ) : dashboard.departures_preview.map((s, i) => (
            <div key={s.id}
              onClick={() => s.guest_id ? openGuest(s.guest_id, {
                tab: "folio",
                action: s.status === "checked_out" ? null : ((s.balance_minor > 0) ? "quick-payment" : "check-out"),
              }) : null}
              className="py-2 border-b border-slate-100 flex items-center gap-3 hover:bg-slate-50 -mx-2 px-2 cursor-pointer min-h-[48px]"
              data-testid={`cc-departure-${i}`}
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium text-[14px] truncate">{s.guest_name}</div>
                <div className="text-[12px] text-slate-500 truncate">{s.code} · {(s.expected_departure || "").slice(0, 10)}</div>
              </div>
              {s.balance_minor > 0 && (
                <StatusChip status="critical" label={money(s.balance_minor)} size="sm"/>
              )}
              <StatusChip status={s.status === "checked_in" ? "pending" : "ready"} label={s.status} size="sm"/>
            </div>
          ))}
        </OperationalCard>
      </div>

      {/* Guest Workspace drawer */}
      <FOGuestWorkspaceContainer
        guestId={activeGuest?.id}
        open={!!activeGuest?.id}
        onOpenChange={(o) => { if (!o) closeGuest(); }}
        initialTab={activeGuest?.tab || "overview"}
        initialAction={activeGuest?.action || null}
      />

      {/* Walk-In */}
      <WalkInDrawer
        open={walkInOpen}
        onOpenChange={setWalkInOpen}
        hotelId={hotelId}
        onSuccess={() => { setWalkInOpen(false); load(); }}
      />

      {/* Search */}
      <GuestSearchDrawer
        open={searchOpen}
        onOpenChange={setSearchOpen}
        hotelId={hotelId}
        onPickGuest={(gid) => { setSearchOpen(false); openGuest(gid); }}
      />
    </div>
  );
}

function scrollToSection(_) { /* placeholder for future drill-downs */ }

function sectionForTab(sectionKey) {
  const map = {
    pending_payments: "folio",
    engineering_holds: "room",
    hk_delays: "room",
    approvals: "overview",
  };
  return map[sectionKey] || "overview";
}
function actionForSection(sectionKey) {
  const map = {
    arrivals: "check-in",
    pending_checkins: "check-in",
    departures: "check-out",
    pending_checkouts: "check-out",
    pending_payments: "quick-payment",
  };
  return map[sectionKey] || null;
}
